import type { ChainMetadata } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { sleep } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import { ChevronRight, X } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import { useQueryStates } from 'nuqs'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useKeyPressEvent } from 'react-use'
import { useDebounce } from 'use-debounce'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import { useTokenAcrossChains } from '@/src/queries/hooks/tokens'
import {
  fromSearchParamsParsers,
  toSearchParamsParsers,
} from '@/src/stores/params'
import {
  useNetworkSelection,
  useTokenSelection,
} from '@/src/stores/token-selection/actions'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import { cn } from '@/src/utils/styles'
import { Button } from '../../ui/button'
import { ChainImg } from '../../ui/chain-img'
import { ListRowButton, ListRowSide, ListRowSideName } from '../../ui/list-row'
import { SearchField } from '../../ui/search-field'
import { NetworkRows } from './components'
import type { ChainToken } from './types'

export const NetworkSelection = ({
  isOpen,
  onRequestClose,
}: {
  isOpen: boolean
  onRequestClose: () => void
}) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 300)

  const ref = useRef<HTMLInputElement>(null)
  const [hasFocused, setHasFocused] = useState(false)

  const {
    close,
    setFilteredNetwork,
    open,
    isOpen: isTokenSelectionOpen,
    setIsAnimatedOpen,
  } = useTokenSelection()

  const { metadata: networkSelectionMetadata, direction } =
    useNetworkSelection()

  const { data: tokenAcrossChains, isLoading: isTokenAcrossChainsLoading } =
    useTokenAcrossChains(
      networkSelectionMetadata?.chainId,
      networkSelectionMetadata?.denom,
    )

  const [{ fromChain, toChain }] = useQueryStates({
    ...toSearchParamsParsers,
    ...fromSearchParamsParsers,
  })

  const oppositeChain = direction === 'from' ? toChain : fromChain

  const token = tokenAcrossChains?.requested_asset

  const { data: chainsMap, isLoading: isChainsMapLoading } =
    useSupportedChainsMap()

  const isLoading = isTokenAcrossChainsLoading || isChainsMapLoading

  const featureFlags = useAtomValue(featureFlagsAtom)
  const privacyUiEnabled = featureFlags.privacyUi

  const { privacyChains, regularChainTokens } = useMemo(() => {
    const chainTokens: ChainToken[] = []

    for (const tokenKey of tokenAcrossChains?.asset_across_chains ?? []) {
      const chain = chainsMap?.get(tokenKey.chain_id)
      if (!chain) continue

      chainTokens.push({
        ...tokenKey,
        ...chain,
      })
    }

    const filtered = chainTokens.filter((token) =>
      token.chain_name.toLowerCase().includes(query.toLowerCase()),
    )

    // If privacy UI is enabled, split into privacy and regular chains
    if (privacyUiEnabled) {
      const privacy = filtered.filter((token) =>
        token.chain_id.includes('namada'),
      )
      const regular = filtered
        .filter((token) => !token.chain_id.includes('namada'))
        .sort((_prev, curr) => {
          const isSelected =
            curr.chain_id === token?.chain_id && curr.denom === token.denom
          const isOppositeChainAvailable = curr.chain_id === oppositeChain

          return isSelected || isOppositeChainAvailable ? 1 : -1
        })

      return { privacyChains: privacy, regularChainTokens: regular }
    }

    // No privacy UI, just sort normally
    const sorted = filtered.sort((_prev, curr) => {
      const isSelected =
        curr.chain_id === token?.chain_id && curr.denom === token.denom
      const isOppositeChainAvailable = curr.chain_id === oppositeChain

      return isSelected || isOppositeChainAvailable ? 1 : -1
    })

    return { privacyChains: [], regularChainTokens: sorted }
  }, [
    tokenAcrossChains?.asset_across_chains,
    chainsMap,
    query,
    token?.chain_id,
    token?.denom,
    oppositeChain,
    privacyUiEnabled,
  ])

  const { networksWithOtherTokens, privacyVaultChain } = useMemo(() => {
    const allChains = Array.from(chainsMap?.values() ?? [])

    // Combine privacy and regular chains for filtering
    const allChainTokens = [...privacyChains, ...regularChainTokens]

    const networks = allChains.filter((chain) => {
      return !allChainTokens.some((token) => token.chain_id === chain.chain_id)
    })

    const filtered = networks.filter(({ chain_name }) =>
      chain_name.toLowerCase().includes(query.toLowerCase()),
    )

    // If privacy UI is enabled, extract Privacy Vault from "other tokens"
    if (privacyUiEnabled) {
      const privacyVault = filtered.find((chain) =>
        chain.chain_id.includes('namada'),
      )
      const nonPrivacy = filtered.filter(
        (chain) => !chain.chain_id.includes('namada'),
      )

      return {
        networksWithOtherTokens: nonPrivacy,
        privacyVaultChain: privacyVault ? [privacyVault] : [],
      }
    }

    return {
      networksWithOtherTokens: filtered,
      privacyVaultChain: [],
    }
  }, [privacyChains, regularChainTokens, chainsMap, query, privacyUiEnabled])

  const { selectToken } = useTokenActions({ direction: direction ?? 'from' })

  const handleSelectNetwork = useCallback(
    ({ chain_id, denom }: Partial<ChainToken>) => {
      selectToken({ chain_id, denom, ticker: token?.ticker })
      onRequestClose()
      setIsAnimatedOpen(false)
      close()
    },
    [selectToken, onRequestClose, setIsAnimatedOpen, close, token?.ticker],
  )

  useKeyPressEvent('Escape', () => {
    if (!isOpen) return

    onRequestClose()

    if (!isTokenSelectionOpen) {
      setIsAnimatedOpen(false)
    }
  })

  const handleSelectOtherNetwork = useCallback(
    async (props: ChainMetadata) => {
      setFilteredNetwork(props)
      onRequestClose()
      if (!isTokenSelectionOpen) {
        await sleep(500)
        open(direction ?? 'from')
      }
    },
    [onRequestClose, setFilteredNetwork, isTokenSelectionOpen, open, direction],
  )

  const [isDelayedOpen] = useDebounce(isOpen, 300)

  return (
    <motion.div
      className={cn(
        'absolute min-w-96 rounded-3xl z-50 inset-0 flex flex-col bg-background',
        {
          hidden: !isOpen && !isDelayedOpen,
        },
      )}
      onAnimationComplete={() => {
        if (!isOpen) {
          setHasFocused(false)
          setQuery('')
        }
        if (isOpen && ref.current && !hasFocused) {
          ref.current.focus()
          setHasFocused(true)
        }
      }}
      animate={{ y: isOpen ? 0 : '100%' }}
      initial={{ y: '100%' }}
      exit={{ y: '100%' }}
      transition={{
        duration: 0.3,
        ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
      }}
    >
      <div className='flex items-center gap-3 px-6 pt-6 pb-2'>
        <SearchField
          dimension='medium'
          data-testid='search-select-network'
          inputProps={{
            placeholder: 'Search networks',
            value: query,
            onChange: (e) => setQuery(e.target.value),
            ref,
          }}
          resetState={() => setQuery('')}
          className='w-full'
        />
        <Button
          type='button'
          size='md'
          onClick={() => {
            onRequestClose()
            setIsAnimatedOpen(false)
          }}
        >
          <X className='size-6 link' />
        </Button>
      </div>
      {/* Privacy Vault Section */}
      {privacyUiEnabled && (
        <div className='flex flex-col px-2 pt-4'>
          {/* If token has Privacy Vault version, show it directly */}
          {privacyChains.length > 0 &&
            privacyChains.map((chain) => (
              <ListRowButton
                key={chain.chain_id}
                onClick={() => handleSelectNetwork(chain)}
                className='py-2'
              >
                <ListRowSide className='flex-row items-center gap-3'>
                  <ChainImg
                    chainId={chain.chain_id}
                    chainImg={chain.chain_img}
                    chainName={chain.chain_name}
                    width={20}
                    height={20}
                    containerClassName='size-8'
                  />
                  <ListRowSideName>{chain.chain_name}</ListRowSideName>
                </ListRowSide>
              </ListRowButton>
            ))}

          {/* If token doesn't have Privacy Vault version, show "Privacy Vault" to select other token */}
          {privacyChains.length === 0 &&
            privacyVaultChain.length > 0 &&
            privacyVaultChain.map((chain) => (
              <ListRowButton
                key={chain.chain_id}
                onClick={() => {
                  handleSelectOtherNetwork?.(chain)
                }}
                className='group py-2'
              >
                <ListRowSide className='flex-row items-center gap-3'>
                  <ChainImg
                    chainId={chain.chain_id}
                    chainImg={chain.chain_img}
                    chainName={chain.chain_name}
                    width={20}
                    height={20}
                    containerClassName='size-8'
                  />
                  <ListRowSideName className='text-secondary group-hover:text-primary group-focus:text-primary transition-colors'>
                    {chain.chain_name}
                  </ListRowSideName>
                </ListRowSide>
                <ListRowSide>
                  <ChevronRight className='size-6 text-secondary group-hover:text-primary group-focus:text-primary transition-colors' />
                </ListRowSide>
              </ListRowButton>
            ))}
        </div>
      )}

      {/* Regular Networks Section */}
      <NetworkRows
        filteredChains={regularChainTokens}
        isLoading={isLoading}
        query={debouncedQuery}
        onSelectNetwork={(chainToken) => {
          if (!chainToken) return

          handleSelectNetwork(chainToken)
        }}
        showAnyNetwork={false}
        header={
          <span className='inline-flex items-center gap-1.5 text-secondary text-sm'>
            Networks with{' '}
            {networkSelectionMetadata?.tokenImg && (
              <Image
                src={networkSelectionMetadata?.tokenImg}
                alt={networkSelectionMetadata?.ticker ?? ''}
                width={16}
                height={16}
                className='rounded-full'
              />
            )}
            {networkSelectionMetadata?.ticker}
          </span>
        }
        shouldUseDirectionalChain={!isTokenSelectionOpen}
        /**
         * Show "networks with other tokens" only when
         * changing network from the swap tool
         */
        showNetworksWithOtherTokens={
          networksWithOtherTokens.length > 0 && !isTokenSelectionOpen
        }
        networksWithOtherTokens={networksWithOtherTokens}
        onSelectOtherNetwork={handleSelectOtherNetwork}
      />
    </motion.div>
  )
}
